import { createTRPCReact } from '@trpc/react-query';
import {
  httpBatchLink,
  httpLink,
  httpSubscriptionLink,
  splitLink,
} from '@trpc/client';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
// react-native-sse — EventSource ponyfill for iOS / Android. Web uses
// the platform-native EventSource and never reaches this import.
// ADR-0082 slice 6 (post-v1): lifts the platform gate that confined
// streaming to web.
//
// Important: react-native-sse exposes its connection state on
// `this.status`, NOT on `this.readyState`. tRPC v11's SSE consumer
// checks `eventSource.readyState === eventSource.CLOSED` to
// distinguish fatal errors from reconnect-able transients (see
// @trpc/server/src/unstable-core-do-not-import/stream/sse.ts:339).
// Without the alias, every disconnect on RN routes as "reconnect"
// and the streaming bubble hangs forever. SseEventSourceAdapter
// below wraps the polyfill with the readyState getter the consumer
// expects.
import NativeEventSource from 'react-native-sse';
// eslint-disable-next-line @nx/enforce-module-boundaries -- App requires API's AppRouter type for tRPC
import type { AppRouter } from '@my-fullstack-app/api';
import { AUTH_KEYS } from '../contexts/AuthContext';
import { setToken } from './token-storage';
import { getAccessToken, setAccessToken } from './auth-store';
import { refreshAccessToken } from './refresh-token';
import { emitAuthExpired } from './auth-events';
import { reportError, reportWarning } from './observability';

const getBaseUrl = () => {
  const fromConfig = Constants.expoConfig?.extra?.apiUrl;
  if (fromConfig) return fromConfig;
  if (typeof process !== 'undefined' && process.env.EXPO_PUBLIC_API_URL)
    return process.env.EXPO_PUBLIC_API_URL;
  // On web (browser), use a relative URL so requests go to the same domain (works on any deployment).
  if (typeof window !== 'undefined') return '';
  return 'http://localhost:3000';
};

export const trpc = createTRPCReact<AppRouter>();

/** Adapts `react-native-sse`'s EventSource shim to the standard
 *  EventSource interface tRPC v11's SSE consumer expects.
 *
 *  The polyfill exposes connection state as `this.status` (matching
 *  its own CONNECTING/OPEN/CLOSED constants). tRPC reads
 *  `eventSource.readyState === eventSource.CLOSED` to decide whether
 *  to treat an error as terminal. Without bridging the two, fatal
 *  network errors look like transient reconnects and the streaming
 *  bubble hangs.
 *
 *  We subclass and add a `readyState` getter (and an instance-side
 *  `CLOSED` constant that mirrors the static one) so the standard-
 *  shaped checks resolve correctly. Everything else
 *  (addEventListener, close, dispatchEvent) passes through. */
class SseEventSourceAdapter extends (NativeEventSource as unknown as new (
  url: string,
  init?: Record<string, unknown> | undefined,
) => Record<string, unknown>) {
  // Both static and instance fields — the standard EventSource API
  // exposes the constants in both places, and tRPC's check uses the
  // instance reference (`eventSource.CLOSED`).
  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSED = 2;
  readonly CONNECTING = 0;
  readonly OPEN = 1;
  readonly CLOSED = 2;

  get readyState(): number {
    // react-native-sse's state alphabet is broader than the standard
    // EventSource API: ERROR = -1, CONNECTING = 0, OPEN = 1,
    // CLOSED = 2. The polyfill DOES use ERROR (set in onerror /
    // ontimeout handlers) for fatal-failure paths — and does NOT
    // transition through CLOSED on those paths unless close() is
    // called explicitly.
    //
    // tRPC's SSE consumer reads `eventSource.readyState ===
    // eventSource.CLOSED` to distinguish fatal errors from
    // reconnect-able transients. If we passed `status` through
    // unchanged, ERROR (-1) would fail that equality and tRPC would
    // keep the consumer waiting for reconnects that never come —
    // the streaming bubble would hang forever (which is the exact
    // bug this adapter exists to fix).
    //
    // Remap ERROR → CLOSED so tRPC's terminal-error path fires; the
    // subscriber's onError handler then unsubscribes, which kills
    // the polyfill's own auto-retry loop before it wastes any work.
    //
    // Also call close() eagerly on the ERROR transition so the
    // polyfill's already-scheduled setTimeout(_pollAgain) is
    // cancelled BEFORE tRPC's async unsubscribe chain arrives. The
    // polyfill schedules a retry synchronously inside its error
    // handler (after dispatching 'error'); without an eager close()
    // the retry can fire under main-thread contention before
    // tRPC's reader.cancel() reaches close(), opening a new XHR
    // that bills the server and drains battery for a torn-down
    // consumer.
    const raw = (this as { status?: number }).status ?? this.CONNECTING;
    if (raw === -1) {
      const self = this as unknown as { close?: () => void };
      if (typeof self.close === 'function') {
        try {
          self.close();
        } catch {
          // close() should be idempotent; ignore any throw
        }
      }
      return this.CLOSED;
    }
    return raw;
  }
}

// Exported for unit testing the 401 refresh / auth-expired escalation
// paths in isolation; production code wires it via createTRPCClient.
export function createFetchWithRefresh(): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit | undefined) => {
    const token = getAccessToken();
    const headers = new Headers(init?.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    let res: Response;
    try {
      res = await fetch(input, { ...init, headers });
    } catch (err) {
      // Network-level failure (DNS, offline, abort). Report so we can spot
      // patterns; the caller still sees the rejection and decides what to do.
      reportError(err, { stage: 'trpc-fetch', url });
      throw err;
    }

    if (res.status === 401) {
      // Defense-in-depth: a 401 on a request we sent WITHOUT a bearer
      // token isn't a session expiry — the caller was never authenticated
      // (e.g. an anonymous user hitting a protectedProcedure). We only set
      // the Authorization header when `token` was truthy, so `!token` here
      // means this request went out unauthenticated. Attempting a refresh
      // would fail (no refresh token) and emitAuthExpired would needlessly
      // tear down the anonymous session — clearConsentForLogout re-shows
      // the GDPR sheet and the persist-scope rehydrate "refreshes" the
      // page — on a loop. Surface the 401 to the caller untouched.
      if (!token) return res;

      const refreshed = await refreshAccessToken();
      const newToken = refreshed.success ? (refreshed.accessToken ?? null) : null;
      if (newToken) {
        await setToken(AUTH_KEYS.accessToken, newToken);
        setAccessToken(newToken);
        headers.set('Authorization', `Bearer ${newToken}`);
        res = await fetch(input, { ...init, headers });
      }
      // Terminal 401: refresh produced no token, OR the retried request
      // also returned 401. Tell AuthContext to clear in-memory state and
      // redirect to sign-in. Don't await — fire and let the caller see
      // the 401 it actually got.
      if (!newToken || res.status === 401) {
        void emitAuthExpired();
      }
    } else if (res.status >= 500) {
      // Server-side failure that wasn't already recovered. Worth a warn so
      // we can correlate with API-side error events.
      reportWarning('trpc 5xx response', { status: res.status, url });
    }
    return res;
  };
}

export function createTRPCClient() {
  const baseUrl = getBaseUrl();
  // Strip any trailing slash so a configured base URL like
  // `https://kiips.vercel.app/` doesn't produce `//trpc`. The empty-string
  // web case (relative URL) is unaffected and stays `/trpc`.
  const url = `${baseUrl.replace(/\/+$/, '')}/trpc`;
  const fetch = createFetchWithRefresh();
  const headers = () => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return trpc.createClient({
    links: [
      // ADR-0082 slice 3 + 6: route subscriptions to the SSE link.
      // The only subscription procedure today is `ai.streamMessage`
      // (assistant streaming).
      //   - Web: platform-native EventSource (tRPC's default).
      //   - RN (iOS / Android): react-native-sse ponyfill — same
      //     interface, fetch-streaming based. Pass per-request auth
      //     headers via the `eventSourceOptions` callback so the
      //     subscription rides the same Bearer token the rest of the
      //     tRPC client uses.
      splitLink({
        condition: (op) => op.type === 'subscription',
        true:
          Platform.OS !== 'web'
            ? httpSubscriptionLink({
                url,
                EventSource:
                  SseEventSourceAdapter as unknown as typeof EventSource,
                // The httpSubscriptionLink generic narrows
                // EventSourceInit to the platform-native type. RN's
                // SSE ponyfill takes a `headers` field on its init
                // object that isn't part of the EventSourceInit
                // standard. Cast through `unknown` — the polyfill
                // accepts what we hand it at runtime.
                eventSourceOptions: (() => ({
                  headers: headers() as Record<string, string>,
                })) as unknown as () => EventSourceInit,
              })
            : httpSubscriptionLink({ url }),
        // Slow procedures get their own HTTP request so a 5xx / timeout in
        // one of them can't take down peer queries (photos, trips, auth) that
        // happened to render in the same tick. Currently:
        //   - travel.*       — Google Directions (Pro) network call; the
        //                      Free static estimator is fast, but keeping
        //                      the whole namespace isolated is simplest
        //   - weather.*      — OpenWeather call, occasional latency spikes
        //   - suggestions.*  — Google Places nearbysearch can be slow for
        //                      large radius queries, and the same query is
        //                      shared across the autocomplete modal +
        //                      post-add suggestion toast
        // Add new paths here as they're identified.
        //
        // tRPC v11 defaults queries to GET and mutations to POST already, so
        // the SW `/trpc/*` GET cache (defined in scripts/build-service-worker.mjs)
        // works without changes here. Mutations stay POST and are owned by
        // SyncManager (the SW does not intercept them).
        false: splitLink({
          condition: (op) =>
            op.path.startsWith('travel.') ||
            op.path.startsWith('weather.') ||
            op.path.startsWith('suggestions.'),
          // tRPC's `FetchEsque` type spells the second arg's optional fields
          // as `init?: RequestInit | undefined`; under EOPT, the native
          // `fetch`'s `RequestInit | undefined` signature is technically a
          // wider arg shape so TS rejects it. Same runtime contract — cast
          // through `as never` since the call is unchanged.
          true: httpLink({ url, fetch: fetch as never, headers }),
          false: httpBatchLink({ url, fetch: fetch as never, headers }),
        }),
      }),
    ],
  });
}
